import { Card, CardContent, Divider, Typography, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDataProvider, useNotify } from "react-admin";
import React, { useEffect, useState } from "react";
import Pagination from "../pagination/pagination";
import ModalConsumer from "../consumer/modalConsumer";
import DeleteCardConfirmDrawer from "../drawer/deleteCardConfirmDrawer.js";
import virtualIcon from "../images/cards/virtual.png";
import metalIcon from "../images/cards/metal.png";
import { AdminRole } from "../../utils/role";

const useStyles = makeStyles((theme) => ({
  root: { backgroundColor: theme.surface.primary },
  content: { padding: 16 },
  headerRow: { marginBottom: 12 },
  item: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 8px' },
  leftRow: { display: 'flex', alignItems: 'center', gap: 12 },
  icon: { width: 32, height: 32, borderRadius: 8, objectFit: 'cover' },
  left: { display: 'flex', flexDirection: 'column' },
  name: { color: '#FDFDFD' },
  meta: { color: theme.text.subtitle, opacity: 0.5 },
  externalMeta: { color: theme.text.subtitle, opacity: 0.85 },
  pagination: { display: 'flex', justifyContent: 'flex-end' },
  deleteBtn: {
    backgroundColor: '#A3A2FF',
    color: '#141417',
    textTransform: 'none',
    borderRadius: 100,
    minHeight: 32,
    height: 32,
    padding: '6px 16px',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: '#A3A2FF',
      opacity: 0.9,
    }
  }
}));

const CardsSection = ({ customerId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [visitedPages, setVisitedPages] = useState([]);
  const perPage = 5;

  useEffect(() => {
    setLoading(true);
    dataProvider.getList('cardsV2', { customerId })
      .then(({ data }) => { setCards(data || []); setError(null); })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [customerId]);

  const start = currentPage * perPage;
  const pageItems = cards.slice(start, start + perPage);

  const getDeleteErrorMessage = (e) => {
    const gqlErr = e?.graphQLErrors?.[0];
    const ext = gqlErr?.extensions || {};
    const reason = ext?.reason || ext?.code;
    const display = ext?.displayErrorMessage;
    const status = ext?.httpStatus || ext?.status || ext?.response?.status;

    if (display) return display;
    if (reason === 'NOT_FOUND' || status === 404) return 'cards.delete.not_found';
    return 'cards.delete.generic_error';
  };

  const onDelete = async (id) => {
    try {
      await dataProvider.delete('cardsV2', { id });
      setCards((prev) => prev.filter(c => c.id !== id));
    } catch (e) {
      console.error('Failed to delete card', e);
      notify(getDeleteErrorMessage(e), { type: 'warning' });
    }
  };

  const formatCardTitle = (c) => {
    const last4 = c?.last4Digit || (c?.id || '').slice(-4);
    // Prefer explicit cardType if provided
    const isPhysical = c?.cardType ? (c.cardType.toUpperCase().includes('PHYSICAL')) : (c?.physicalCardStatus && c.physicalCardStatus !== 'NOT_PHYSICAL_CARD');
    const prefix = isPhysical ? 'Metal' : 'Virtual';
    return `${prefix} •••• ${last4}`;
  };
  const getCardIcon = (c) => {
    const isPhysical = c?.cardType ? (c.cardType.toUpperCase().includes('PHYSICAL')) : (c?.physicalCardStatus && c.physicalCardStatus !== 'NOT_PHYSICAL_CARD');
    return isPhysical ? metalIcon : virtualIcon;
  };

  return (
    <Card elevation={0} className={classes.root}>
      <CardContent className={classes.content}>
        <Typography variant="h6" className={classes.headerRow}>Cards</Typography>
        {cards.length === 0 && !loading && !error && (
          <Typography variant="body1">No items found.</Typography>
        )}
        {pageItems.map((c, idx) => (
          <React.Fragment key={c.id}>
            <div className={classes.item}>
              <div className={classes.leftRow}>
                <img alt="" src={getCardIcon(c)} className={classes.icon} />
                <div className={classes.left}>
                  <Typography className={classes.name}>{formatCardTitle(c)}</Typography>
                  { (c.externalCardId || c.cardToken) && (
                    <Typography className={classes.externalMeta}>External Card ID: {c.externalCardId || c.cardToken}</Typography>
                  )}
                </div>
              </div>
              <AdminRole>
                <ModalConsumer>
                  {({ showModal }) => (
                    <Button variant="contained" className={classes.deleteBtn} onClick={() => showModal(DeleteCardConfirmDrawer, { onConfirm: () => onDelete(c.id) })}>Delete</Button>
                  )}
                </ModalConsumer>
              </AdminRole>
            </div>
            {idx !== pageItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        {cards.length > perPage && (
          <div className={classes.pagination}>
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              perPage={perPage}
              total={cards.length}
              visitedPages={visitedPages}
              setVisitedPages={setVisitedPages}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CardsSection;


